/**
 * ProjectContext (WI-6a-3, SK-HOME-1 / DS-1g-05 §5.1, §8.3) —
 * TW-1g-PROJECT-INFER, DS-1g-07.
 *
 * The shell-level seam that carries the provisioned client config
 * (`{tenant_id, project_ids, role, bearer}`, §8.3 install) plus the
 * user-selected `project_ref` into every emission-composing surface
 * (Capture→Save, FeedbackTab) and the visibility gates (ProjectSelector's
 * project list, the Advanced tab). Replaces the fixed placeholder tenant
 * placeholder the shell carried before this WI.
 *
 * `project_ref` is EXCLUSIVELY user-selected, RBAC-scoped, and NEVER
 * inferred/auto-created (TW-1g-PROJECT-INFER) — the only value it can hold
 * is one of `config.project_ids`, enforced by `selectedProjectRef()`
 * (ProjectSelector's single accessor). This context never picks a default
 * project; a fresh session starts with `project_ref === null` and the
 * human must pick before Save carries a project.
 *
 * The provisioned config is a LOCAL FIXTURE in Stage A — the real
 * session-bootstrap endpoint (JWT claims → scope) is V0.5 auth work
 * (design §10), not invented here.
 */

import { createContext } from "preact";
import { useContext } from "preact/hooks";
import type { ProvisionedClientConfig } from "../role/advanced-gate";
import type { WorkosCurrentUser } from "../auth/workos-identity";

export interface ProjectContextValue {
  /** Verified WorkOS identity. Authorization reads org/role only from this
   * server-verifiable current-user seam, never from editable client input. */
  current_user: WorkosCurrentUser;
  /** The provisioned client config mirrored per pilot device (§8.3) —
   * used ONLY for rendering decisions (selector list, tab visibility),
   * never as a security boundary (server scope-checks are authoritative). */
  config: ProvisionedClientConfig;
  /** The current user-selected project_ref, or `null` until the human
   * picks one. Only ever a member of `config.project_ids`. */
  project_ref: string | null;
  /** Records the human's project pick (from ProjectSelector's onSelect). */
  setProjectRef: (project_ref: string) => void;
}

/** Stage-A local provisioned-config fixture (design §8.3 install mirror).
 * Swapped for the real session-bootstrap scope in V0.5 with zero surface
 * change — the shape is the frozen `ProvisionedClientConfig`. */
export const FIXTURE_CLIENT_CONFIG: ProvisionedClientConfig = {
  tenant_id: "tenant-a",
  // Visible project slugs (the ProjectSelector pill's options) — plug names
  // until the real provisioned project names load at session bootstrap (V0.5,
  // zero surface change). `tenant_id` above is the invisible isolation key,
  // never rendered. NB: the coordinator's authorized project_ids must match
  // this set exactly (a member actor is scoped per-project).
  project_ids: ["Fray", "Foster"],
  role: "tenant_admin",
  bearer: "dev-bearer",
};

/** Dry current-user fixture. The live value is produced by
 * verifyAndMapWorkosToken after AuthKit callback verification. */
export const FIXTURE_CURRENT_USER: WorkosCurrentUser = {
  user_id: "user_joey",
  org_id: "org_test_FAKE_mercer",
  role: "admin",
};

export const ProjectContext = createContext<ProjectContextValue | null>(null);

/** Reads the ProjectContext or throws — a surface that composes an
 * emission (Save, Feedback) MUST be mounted under the provider; a missing
 * provider is a wiring bug, never a silent placeholder-tenant fallback. */
export function useProjectContext(): ProjectContextValue {
  const ctx = useContext(ProjectContext);
  if (ctx === null) {
    throw new Error("useProjectContext: no ProjectContext provider in the tree");
  }
  return ctx;
}
